import React from 'react';
import PropTypes from 'prop-types';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Label from '../Label';
import { useTheme } from '@mui/material/styles';


/**
 * DatePicker - Themed, reusable date picker component with validation.
 *
 * @param {object} props
 * @param {string} props.name - Name of the field (for form handling).
 * @param {string} [props.label] - Label for the field.
 * @param {Date|null} props.value - Selected date value.
 * @param {function} props.onChange - Change handler (date) => void.
 * @param {boolean} [props.required] - Required field.
 * @param {boolean} [props.error] - Error state for manual validation.
 * @param {string} [props.helperText] - Helper text for manual validation.
 * @param {string} [props.format] - Date format (default: 'MM/dd/yyyy').
 * @param {boolean} [props.disabled] - Disable the picker.
 * @param {object} [props.sx] - MUI sx prop for custom styles.
 * @returns {JSX.Element}
 */

const DatePicker = ({
  name,
  label,
  value,
  onChange,
  required = false,
  error = false,
  helperText = '',
  format = 'MM/dd/yyyy',
  disabled = false,
  sx = {},
  ...rest
}) => {
  const theme = useTheme();

  const customInputStyles = {
    '& .MuiPickersInputBase-root': {
      borderRadius: '5px',
      color: theme.palette.text.primary,
      boxShadow: `0px -0.04px 0px 2.7px ${theme.palette.textInput?.borderShadowColor || 'transparent'}`,
      '& fieldset': {
        borderRadius: '3px',
        borderColor: theme.palette.textInput?.borderColor || theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
      '&:hover': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused': {
        borderColor: theme.palette.primary.dark,
        boxShadow: `0px -0.2px 0px 1px ${theme.palette.primary.main}`,
      },
      '&.Mui-error fieldset': {
        borderColor: theme.palette.error.main,
      },
      '&.Mui-disabled': {
        opacity: 0.7,
      },
    },
    '& .MuiPickersInputBase-input': {
      color: theme.palette.text.primary,
    },
    '& .MuiIconButton-root': {
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.primary.main,
        backgroundColor: 'transparent',
      },
    },
    '& .MuiPickersInputBase-sectionsContainer': {
      color: theme.palette.text.primary,
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {label && <Label htmlFor={name} required={required}>{label}</Label>}
      <MuiDatePicker
        value={value}
        onChange={onChange}
        format={format}
        disabled={disabled}
        slotProps={{
          textField: {
            name,
            required,
            error,
            helperText,
            fullWidth: true,
            size: 'small',
            variant: 'outlined',
            sx: { ...sx, ...customInputStyles },
            ...rest,
          },
          popper: {
            sx: {
              '& .MuiPaper-root': {
                color: theme.palette.text.primary,
                boxShadow: theme.shadows[4],
              },
              '& .MuiPickersDay-root': {
                color: theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
                '&.MuiPickersDay-today': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiPickersCalendarHeader-label': {
                color: theme.palette.text.primary,
              },
              '& .MuiPickersArrowSwitcher-button': {
                color: theme.palette.text.secondary,
              },
              '& .MuiDayCalendar-weekDayLabel': {
                color: theme.palette.text.secondary,
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

DatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  format: PropTypes.string,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
};

export default DatePicker;